<template>
  <vue-good-table
    :columns="columns"
    :line-numbers="false"
    :rows="rows"
    :total-rows="total"
    :sort-options="{ enabled: true }"
    :pagination-options="{ enabled: true }"
    style-class="vgt-table border-0 attendance-table"
    @on-sort-change="$emit('sort-change', $event)"
    @on-page-change="$emit('page-change', $event)"
    @on-per-page-change="$emit('per-page-change', $event)"
    styleClass="vgt-table condensed"
  >
    <div slot="emptystate" class="attendance-empty">
      <div v-if="loading" class="empty-content">
        <div class="spinner-border text-danger" role="status">
          <span class="sr-only">Loading...</span>
        </div>
        <p class="empty-title mt-3 mb-1">Loading attendance data</p>
        <p class="empty-text mb-0">Please wait while the schedule list is being prepared.</p>
      </div>
      <div v-else class="empty-content">
        <span class="empty-icon fe fe-calendar"></span>
        <p class="empty-title mb-1">No attendance schedule found</p>
        <p class="empty-text mb-0">Attendance appears here when your approved IDP training has a scheduled batch.</p>
      </div>
    </div>

    <template slot="table-row" slot-scope="props">
      <span v-if="props.column.field === 'no'" class="row-number">
        {{ getRowNumber(props.index) }}
      </span>

      <span v-else-if="props.column.field === 'title'" class="training-title">
        {{ props.formattedRow.title || '-' }}
      </span>

      <span v-else-if="props.column.field === 'event'" class="d-flex flex-column">
        <div v-for="evt in props.row.events || []" :key="evt.id" class="mb-2">
          <div class="batch-pill">
            {{ formatBatchLabel(evt) }}
          </div>
        </div>
        <div v-if="!(props.row.events && props.row.events.length)">
          <small class="text-muted">No batches</small>
        </div>
      </span>

      <span v-else-if="props.column.field === 'penyelenggara'" class="d-flex flex-column">
        <div v-for="evt in props.row.events || []" :key="evt.id" class="mb-2">
          {{ formatPenyelenggara(evt.penyelenggara) }}
        </div>
        <small v-if="!(props.row.events && props.row.events.length)" class="text-muted">-</small>
      </span>

      <span v-else-if="props.column.field === 'schedule'" class="d-flex flex-column">
        <div v-for="evt in props.row.events || []" :key="evt.id" class="mb-2">
          <div class="schedule-list mb-0">
            <div v-for="s in evt.schedules || []" :key="s.id" class="schedule-item">
              {{ formatDate(s.date) }} | {{ formatTimeToHHMM(s.start_time) }} - {{ formatTimeToHHMM(s.end_time) }}
            </div>
          </div>
        </div>
        <div v-if="!(props.row.events && props.row.events.length)">
          <small class="text-muted">No schedules</small>
        </div>
      </span>

      <span v-else-if="props.column.field === 'status'" class="d-flex flex-column">
        <div v-for="evt in props.row.events || []" :key="evt.id" class="mb-2">
          <span :class="getTrainingStatusClass(evt.status)">{{ snakeToTitle(evt.status) }}</span>
        </div>
        <div v-if="!(props.row.events && props.row.events.length)">
          <small class="text-muted">-</small>
        </div>
      </span>

      <span v-else-if="props.column.field === 'action'" class="d-flex flex-column align-items-center">
        <div v-for="evt in props.row.events || []" :key="evt.id" class="mb-2 d-flex gap-2">
          <b-button
            class="action-button"
            variant="outline-primary"
            size="sm"
            title="View batch"
            @click="$emit('view-event', { id: evt.id })"
          >
            <span class="fe fe-eye"></span>
          </b-button>
        </div>
        <div v-if="!(props.row.events && props.row.events.length)">
          <small class="text-muted">-</small>
        </div>
      </span>

      <span v-else class="d-flex align-items-center justify-content-start">
        {{ props.formattedRow[props.column.field] }}
      </span>
    </template>

    <template slot="pagination-bottom">
      <div class="d-flex justify-content-between align-items-center mt-3 px-3 pb-2">
        <div class="text-muted">
          Showing
          <strong>{{ showingFrom }} - {{ showingTo }}</strong>
          of <strong>{{ total }}</strong> items
        </div>

        <b-pagination
          :value="page"
          :total-rows="total"
          :per-page="pageSize"
          first-number
          last-number
          align="right"
          size="md"
          @input="$emit('page-change-from-bv', $event)"
        />
      </div>
    </template>
  </vue-good-table>
</template>

<script>
import { props } from "vue2-dropzone";
import { getTrainingStatusClass, snakeToTitle, formatDate, formatTimeToHHMM } from "../utils/utils";
import { formatBatchLabel, formatPenyelenggara } from "@/utils/batchIdentity";

export default {
  name: "DataTable",
  props: {
    rows: Array,
    total: Number,
    showingFrom: Number,
    showingTo: Number,
    page: Number,
    pageSize: Number,
    loading: Boolean,
  },

  data() {
    return {
      columns: [
        {
          label: "No",
          field: "no",
          sortable: false,
          width: "72px",
          thClass: "table-header text-muted font-weight-bold text-center",
          tdClass: "color-secondary text-center",
        },
        {
          label: "Training Title",
          field: "title",
          sortable: false,
          thClass: "table-header text-muted font-weight-bold",
          tdClass: "color-secondary",
        },
        {
          label: "Batch",
          field: "event",
          sortable: false,
          width: "140px",
          thClass: "table-header text-muted font-weight-bold",
          tdClass: "color-secondary",
        },
        {
          label: "Penyelenggara",
          field: "penyelenggara",
          sortable: false,
          thClass: "table-header text-muted font-weight-bold",
          tdClass: "color-secondary",
        },
        {
          label: "Schedule",
          field: "schedule",
          sortable: false,
          thClass: "table-header text-muted font-weight-bold",
          tdClass: "color-secondary",
        },
        {
          label: "Status",
          field: "status",
          sortable: false,
          width: "180px",
          thClass: "table-header text-muted font-weight-bold",
          tdClass: "color-secondary",
        },
        {
          label: "Action",
          field: "action",
          sortable: false,
          thClass: "table-header text-muted font-weight-bold w-action",
          tdClass: "color-secondary text-center",
        },
      ],
    };
  },

  methods: {
    getTrainingStatusClass,
    snakeToTitle,
    formatDate,
    formatTimeToHHMM,
    formatBatchLabel,
    formatPenyelenggara,
    getRowNumber(index) {
      return (Number(this.page || 1) - 1) * Number(this.pageSize || 10) + index + 1;
    },
  },
};
</script>

<style scoped>
.w-action {
  width: 140px;
  min-width: 140px;
  max-width: 140px;
}

:deep(.custom-switch .custom-control-label) {
  display: flex !important;
  align-items: center !important;
  cursor: pointer;
}

:deep(.custom-switch .custom-control-input:not(:checked) ~ .custom-control-label::before) {
  background-color: #6c757d !important;
  border-color: #6c757d !important;
}

:deep(.custom-switch .custom-control-input:checked ~ .custom-control-label::before) {
  background-color: #28a745 !important;
  border-color: #28a745 !important;
}

:deep(.custom-switch .custom-control-input:checked ~ .custom-control-label::after) {
  background-color: #fff !important;
}

:deep(.table-header) {
  background: #f8fafc !important;
  color: #8a9bb8 !important;
  font-size: 12px !important;
  letter-spacing: 0;
  padding: 14px 16px !important;
  text-transform: uppercase;
}

:deep(.attendance-table) {
  border: 1px solid #e3ebf6 !important;
  border-radius: 8px;
  overflow: hidden;
}

:deep(.vgt-table td, .vgt-table th) {
  border-color: #edf2f9 !important;
  font-size: 13px !important;
  vertical-align: middle !important;
}

:deep(.vgt-table td) {
  padding: 16px !important;
}

.schedule-list {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.schedule-item {
  margin: 0;
  padding: 0;
  color: #495057;
}

.row-number {
  color: #667085;
  font-weight: 700;
}

.training-title {
  color: #12263f;
  display: block;
  font-weight: 700;
  line-height: 1.35;
  min-width: 220px;
}

.batch-pill {
  background: #eff6ff;
  border-radius: 6px;
  color: #2563eb;
  display: inline-flex;
  font-size: 12px;
  font-weight: 700;
  padding: 5px 9px;
}

.action-button {
  align-items: center;
  display: inline-flex;
  height: 32px;
  justify-content: center;
  width: 36px;
}

.attendance-empty {
  padding: 48px 16px;
  text-align: center;
}

.empty-content {
  align-items: center;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.empty-icon {
  align-items: center;
  background: #fff1f3;
  border-radius: 999px;
  color: #e63757;
  display: inline-flex;
  font-size: 24px;
  height: 56px;
  justify-content: center;
  margin-bottom: 14px;
  width: 56px;
}

.empty-title {
  color: #12263f;
  font-size: 15px;
  font-weight: 700;
}

.empty-text {
  color: #8a9bb8;
  font-size: 13px;
  max-width: 480px;
}
</style>
